import React, { useCallback } from 'react'
import { generatePath, matchPath, useHistory, useLocation } from 'react-router-dom'
import {
  DEFENDER_POLICIES_PATH,
  DISCOVER_APPLICATIONS_PATH,
  DISCOVER_DATA_PATH,
  DISCOVER_PATH,
  DISCOVER_RISK_PATH,
  ENGAGE_ACTION_LOG_PATH,
  ENGAGE_ACTIONS_PATH,
  ENGAGE_DISTRIBUTION_LISTS_PATH,
  EMPLOYEES_PATH,
  GROUPS_PATH,
  INTEGRATIONS_PATH,
  INTUNE_DEPLOYMENT_PROFILES_PATH,
  INTUNE_DEVICE_POLICIES_PATH,
  SECURE_ALERTS_PATH,
  SECURE_POLICIES_PATH,
  SECURE_POSTURE_COMPANY_PATH,
  SECURE_POSTURE_PATH,
  REPORTS_MFA_PATH,
  REPORTS_SUMMARY_PATH,
  REPORTS_THREATS_PATH,
  REPORTS_SCHEDULED_PATH,
} from '../../app/router/constants'

export function removeCSSClass(ele, cls) {
  const reg = new RegExp(`(\\s|^)${cls}(\\s|$)`)
  ele.className = ele.className.replace(reg, ' ') // eslint-disable-line no-param-reassign
}

export function addCSSClass(ele, cls) {
  ele.classList.add(cls)
}

export const toAbsoluteUrl = (pathname) => pathname

export function setupAxios(axios) {
  axios.interceptors.request.use(
    (config) =>
      /*
      const {
        auth: { authToken }
      } = store.getState();

      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }
      */

      config,
    (err) => Promise.reject(err),
  )
}

/*  removeStorage: removes a key from localStorage and its sibling expiry key
    params:
        key <string>     : localStorage key to remove
    returns:
        <boolean> : telling if operation succeeded
 */
export function removeStorage(key) {
  try {
    localStorage.removeItem(key)
    localStorage.removeItem(`${key}_expiresIn`)
  } catch (e) {
    console.error(`removeStorage: Error removing key [${key}] from localStorage: ${JSON.stringify(e)}`)
    return false
  }
  return true
}

/*  getStorage: retrieves a key from localStorage previously set with setStorage().
    params:
        key <string> : localStorage key
    returns:
        <string> : value of localStorage key
        null : in case of expired key or failure
 */
export function getStorage(key) {
  const now = Date.now() // epoch time, lets deal only with integer
  // set expiration for storage
  let expiresIn = localStorage.getItem(`${key}_expiresIn`)
  if (expiresIn === undefined || expiresIn === null) {
    expiresIn = 0
  }

  expiresIn = Math.abs(expiresIn)
  if (expiresIn < now) {
    // Expired
    removeStorage(key)
    return null
  }
  try {
    const value = localStorage.getItem(key)
    return value
  } catch (e) {
    console.error(`getStorage: Error reading key [${key}] from localStorage: ${JSON.stringify(e)}`)
    return null
  }
}
/*  setStorage: writes a key into localStorage setting a expire time
    params:
        key <string>     : localStorage key
        value <string>   : localStorage value
        expires <number> : number of seconds from now to expire the key
    returns:
        <boolean> : telling if operation succeeded
 */
export function setStorage(key, value, expires) {
  if (expires === undefined || expires === null) {
    // eslint-disable-next-line no-param-reassign
    expires = 24 * 60 * 60 // default: seconds for 1 day
  }

  const now = Date.now() // milliseconds since epoch time, lets deal only with integer
  const schedule = now + expires * 1000
  try {
    localStorage.setItem(key, value)
    localStorage.setItem(`${key}_expiresIn`, schedule)
  } catch (e) {
    console.error(`setStorage: Error setting key [${key}] in localStorage: ${JSON.stringify(e)}`)
    return false
  }
  return true
}

export const sortingCarets = (order) => {
  if (!order) {
    return (
      <span className="carets">
        <span className="icon-md fa fa-arrow-down font-color-1" />
        &nbsp;
        <span className="icon-md fa fa-arrow-up font-color-1" />
      </span>
    )
  }
  if (order === 'asc') {
    return (
      <span className="carets">
        <span className="icon-md fa fa-arrow-down" />
        &nbsp;
        <span className="icon-md fa fa-arrow-up font-color-primary" />
      </span>
    )
  }
  if (order === 'desc') {
    return (
      <span className="carets">
        <span className="icon-md fa fa-arrow-down font-color-primary" />
        &nbsp;
        <span className="icon-md fa fa-arrow-up" />
      </span>
    )
  }
  return null
}

// Check if string is null or whitespace
export const isNullOrWhiteSpace = (str) => !str || str.length === 0 || /^\s*$/.test(str)

/**
 * waits before performing next action.
 * Has to be called with await, e.g. await sleep(2000); // stops execution for 2 seconds
 * @param {number} ms Number of milliseconds to wait
 */
export const delay = async (ms) =>
  // eslint-disable-next-line no-return-await, no-promise-executor-return
  await new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Map of page path to permission name
 */
export const PAGE_TO_PERMISSION = {
  '/billing': 'billing',
  '/companies': 'companies',
  '/discovery': 'discover',
  [DISCOVER_PATH]: 'discover',
  '/discover/data': 'discover',
  [DISCOVER_DATA_PATH]: 'discover',
  '/discover/risk': 'discover',
  [DISCOVER_RISK_PATH]: 'discover',
  '/discover/applications': 'discover',
  [DISCOVER_APPLICATIONS_PATH]: 'discover',
  '/engage': 'engage',
  '/engage/user-management': 'engage',
  '/engage/action-log': 'engage',
  '/engage/distribution-lists': 'engage',
  [ENGAGE_DISTRIBUTION_LISTS_PATH]: 'engage',
  '/integrations': 'integrations',
  '/licenses': 'licenseCenter',
  '/look-and-feel': 'lookAndFeel',
  '/secure/alerts': 'secure',
  '/secure/security-posture-menu': 'secure',
  '/secure/security-posture': 'secure',
  '/secure/policy-management': 'secure',
  '/secure/defender': 'secure',
  '/secure/intune': 'intune',
  '/secure/entra-id': 'secure',
  '/reports/mfa': 'secure',
  [REPORTS_MFA_PATH]: 'secure',
  '/reports/threats': 'secure',
  [REPORTS_THREATS_PATH]: 'secure',
  '/reports/summary': 'secure',
  [REPORTS_SUMMARY_PATH]: 'secure',
  '/scheduled-reports': 'secure',
  [REPORTS_SCHEDULED_PATH]: 'always',
  '/users': 'systemUsers',
}

export const INITIAL_MENU_PERMISSION = {
  secure: true,
  'secure/alerts': true,
  'secure/security-posture-menu': true,
  'secure/security-posture': true,
  'secure/policy-management': true,
  'secure/defender': true,
  'secure/intune': true,
  'secure/entra-id': true,
  'reports/mfa': true,
  'reports/licensing': true,
  'reports/threats': true,
  'reports/summary': true,
  discovery: true,
  'scheduled-reports': true,
  'discover/data': true,
  'discover/risk': true,
  'discover/applications': true,
  engage: true,
  'engage/user-management': true,
  'engage/action-log': true,
  'engage/distribution-lists': true,
  backup: true,
  'system-configuration': true,
  companies: true,
  security_training: true,
  'open-ticket': true,
  chat: true,
  marketing: true,
  employees: true,
  integrations: true,
  licenses: true,
  users: true,
  billing: true,
  support: true,
  'user-profile': false,
  notifications: false,
  'look-and-feel': true,
  development: true,
  reports: true,
}

export const ROUTES_PRIORITY_QUEUE = ['/secure/security-posture', '/discovery', '/engage/user-management']

export const displayPage = (pageInput, permissions, options = {}) => {
  let page = pageInput
  if (Array.isArray(pageInput)) {
    page = pageInput.at(0)
  }

  const { customers } = options

  // Employees : Set to Manage if technician has manage access to Discover, Engage, Secure or License Center, otherwise Disabled. remove
  if (page === '/employees') {
    return (
      permissions.discover === 'manage' ||
      permissions.secure === 'manage' ||
      permissions.engage === 'manage' ||
      permissions.licenseCenter === 'manage'
    )
  }

  // special case // Agent: Set to Manage if technician has >>>Manage access to Discover, otherwise disabled
  if ((page === '/discover/data' || page === DISCOVER_DATA_PATH) && permissions.discover === 'readOnly') {
    return false
  }
  // SaaS Apps:  App Catalogue: Disabled if technician has Discover Disabled/Read only, otherwise is set to Manage.
  if (
    (page === '/discover/applications' || page === DISCOVER_APPLICATIONS_PATH) &&
    permissions.discover === 'readOnly'
  ) {
    return false
  }

  // Secure Read-only, hide Posture Template menu item, no point in showing it.
  if (page === '/secure/posture-templates' && permissions.secure !== 'manage') {
    return false
  }

  // hide intune templates page if permission is not manage
  if (
    (page === '/secure/intune/device-management/templates' || page === '/secure/intune/onboarding/templates') &&
    permissions.intune !== 'manage'
  ) {
    return false
  }

  // hide defender and cap templates page if secure permission is not manage
  if (
    (page === '/secure/defender/templates' || page === '/secure/policy-management/templates') &&
    permissions.secure !== 'manage'
  ) {
    return false
  }

  // check access to integrations mapping page
  const mappingPages = [
    '/integrations/:integrationId/connectwise',
    '/integrations/:integrationId/autotask',
    '/integrations/:integrationId/microsoft-csp',
  ]
  if (customers?.length && mappingPages.includes(page)) {
    const hasPermission = permissions[PAGE_TO_PERMISSION['/integrations']] !== 'disabled'
    return hasPermission
  }

  const licenseFamily = PAGE_TO_PERMISSION[page]
  if (licenseFamily === 'always') {
    return true
  }
  return permissions[licenseFamily] !== 'disabled'
}

export const getLandingPagePath = (userPermissions = {}) => {
  let landingPagePath = ''
  // eslint-disable-next-line no-restricted-syntax
  for (const route of ROUTES_PRIORITY_QUEUE) {
    if (userPermissions[PAGE_TO_PERMISSION[route]] !== 'disabled') {
      landingPagePath = route
      return landingPagePath
    }
  }

  return '/welcome'
}

export const resetDefaultPermission = (menuConfig) => {
  menuConfig.aside.items.forEach((menuItem) => {
    menuItem.visible = INITIAL_MENU_PERMISSION[menuItem.page] // eslint-disable-line no-param-reassign
    if (menuItem.submenu) {
      menuItem.submenu.forEach((submenuItem) => {
        submenuItem.visible = INITIAL_MENU_PERMISSION[submenuItem.page] // eslint-disable-line no-param-reassign
      })
    }
  })
}

/**
 * Paths used to display <IntegrationRefreshButton /> and <CustomerMappingAlert />
 */
export const syncMappedPaths = [/secure/, /engage/, /employees/, /integrations/, /reports/]
export const isSyncPath = () =>
  syncMappedPaths.some((path) => window.location.pathname.match(path)) &&
  !window.location.pathname.includes('templates')

// Add const here for path bases to compare later
// customer routes have to include 'companySelectorId' param
export const CUSTOMER_ROUTES = [
  SECURE_POSTURE_PATH,
  SECURE_POSTURE_COMPANY_PATH,
  SECURE_ALERTS_PATH,
  INTEGRATIONS_PATH,
  SECURE_POLICIES_PATH,
  ENGAGE_ACTIONS_PATH,
  ENGAGE_ACTION_LOG_PATH,
  ENGAGE_DISTRIBUTION_LISTS_PATH,
  EMPLOYEES_PATH,
  DEFENDER_POLICIES_PATH,
  INTUNE_DEVICE_POLICIES_PATH,
  INTUNE_DEPLOYMENT_PROFILES_PATH,
  GROUPS_PATH,
  DISCOVER_PATH,
  DISCOVER_RISK_PATH,
  DISCOVER_DATA_PATH,
  DISCOVER_APPLICATIONS_PATH,
  REPORTS_MFA_PATH,
  REPORTS_THREATS_PATH,
  REPORTS_SUMMARY_PATH,
  REPORTS_SCHEDULED_PATH,
]

export const useCustomerRoutePath = () => {
  const { pathname, search } = useLocation()
  const history = useHistory()

  const { path, params } = CUSTOMER_ROUTES.reduce(
    (result, value) =>
      matchPath(pathname, {
        path: value,
        exact: true,
      }) || result,
    {},
  )
  const { companySelectorId, tabKey } = params || {}
  const navigate = useCallback(
    (customerId, action = 'push') => {
      if (!path) {
        return undefined
      }

      const redirectPath = `${generatePath(path, {
        tabKey,
        companySelectorId: customerId,
      })}${search}`

      if (action === 'replace') {
        return history.replace(redirectPath)
      }

      return history.push(redirectPath)
    },
    [path, search, history, tabKey],
  )

  return {
    navigate,
    path,
    companySelectorId: Number(companySelectorId) || undefined,
  }
}
